import React, { useState, useEffect, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// GENERAL SETTINGS - COMPANY CONFIGURATION (FIXED BUTTONS, SCROLLABLE FORM)
// ─────────────────────────────────────────────────────────────────────────────

const GeneralSettings = ({ isDark = true }) => {
  const theme = isDark ? 'dark' : 'light';
  
  const colors = {
    background: isDark ? "#0d1117" : "#f9fafb",
    surface: isDark ? "#141824" : "#ffffff",
    surfaceHover: isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.02)",
    surfaceLighter: isDark ? "#1a2035" : "#f3f4f6",
    border: isDark ? "#1e2740" : "#e5e7eb",
    borderLight: isDark ? "#1a2035" : "#f3f4f6",
    textPrimary: isDark ? "#f1f5f9" : "#111827",
    textSecondary: isDark ? "#94a3b8" : "#6b7280",
    textMuted: isDark ? "#64748b" : "#9ca3af",
    inputBg: isDark ? "#0f1520" : "#ffffff",
    inputBorder: isDark ? "#1e2740" : "#d1d5db",
    inputFocus: "#3b82f6",
    buttonPrimaryBg: "#4a6cf7",
    buttonPrimaryColor: "#ffffff",
    buttonSecondaryBg: isDark ? "#1a2035" : "#f3f4f6",
    buttonSecondaryColor: isDark ? "#94a3b8" : "#6b7280",
    success: "#22c55e",
    successBg: isDark ? "rgba(34,197,94,0.15)" : "#e8f5e9",
    divider: isDark ? "#1e2740" : "#e5e7eb",
    cardBg: isDark ? "#141824" : "#ffffff",
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Info
    companyName: "Zaptodoor",
    contact: "+91 9200018690",
    headerText: "Zaptodoor",
    callNumber: "+91 9200018690",
    email: "info@zaptodoor.com",
    domain: "https://zaptodoor.com",
    salesEmail: "info@zaptodoor.com",
    address: "Gwalior Smart city incubation center, Moti Mahal, Gwalior, Madhya Pradesh",
    
    // Social Media
    facebook: "https://www.facebook.com/profile.php?id=61570458",
    twitter: "https://x.com/zap_to_door",
    instagram: "https://www.instagram.com/zaptodoor",
    linkedin: "https://linkedin.com",
    youtube: "https://www.youtube.com/@zaptodoor",
    whatsapp: "https://wa.me/message/WWB7OGYJ5NEUK1",
    
    // Footer
    footerText: "",
  });

  const [originalData, setOriginalData] = useState({});

  // ─────────────────────────────────────────────────────────────────────────────
  // API FUNCTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      setTimeout(() => {
        const mockData = {
          companyName: "Zaptodoor",
          contact: "+91 9200018690",
          headerText: "Zaptodoor",
          callNumber: "+91 9200018690",
          email: "info@zaptodoor.com",
          domain: "https://zaptodoor.com",
          salesEmail: "info@zaptodoor.com",
          address: "Gwalior Smart city incubation center, Moti Mahal, Gwalior, Madhya Pradesh",
          facebook: "https://www.facebook.com/profile.php?id=61570458",
          twitter: "https://x.com/zap_to_door",
          instagram: "https://www.instagram.com/zaptodoor",
          linkedin: "https://linkedin.com",
          youtube: "https://www.youtube.com/@zaptodoor",
          whatsapp: "https://wa.me/message/WWB7OGYJ5NEUK1",
          footerText: "",
        };
        setFormData(mockData);
        setOriginalData(mockData);
        setLoading(false);
      }, 500);
      
    } catch (error) {
      console.error("Fetch settings error:", error);
      showToast("Failed to load settings", "error");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      showToast("Settings saved successfully");
      setOriginalData({ ...formData });
    } catch (error) {
      console.error("Save settings error:", error);
      showToast("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = () => {
    setFormData({ ...originalData });
    showToast("Reset to saved settings", "info");
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STYLES - FIXED BUTTONS AT BOTTOM, SCROLLABLE FORM CONTENT
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Fixed header height (adjust based on your actual header if any)
  const HEADER_OFFSET = 0; // Change this if you have a global app header
  
  const styles = {
    // Main wrapper - takes full viewport, handles scrolling
    container: {
      height: `calc(100vh - ${HEADER_OFFSET}px)`,
      background: colors.background,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
    },
    // Scrollable content area
    scrollableContent: {
      flex: 1,
      overflowY: "auto",
      padding: "24px 32px",
      // Smooth scrolling
      scrollBehavior: "smooth",
    },
    // Fixed footer with buttons
    fixedFooter: {
      position: "sticky",
      bottom: 0,
      left: 0,
      right: 0,
      background: colors.background,
      borderTop: `1px solid ${colors.divider}`,
      padding: "16px 32px",
      zIndex: 100,
      // Optional: add subtle shadow for better separation
      boxShadow: isDark ? "0 -4px 12px rgba(0,0,0,0.3)" : "0 -4px 12px rgba(0,0,0,0.05)",
    },
    header: {
      marginBottom: "24px",
    },
    title: {
      fontSize: "24px",
      fontWeight: 700,
      color: colors.textPrimary,
      margin: 0,
      marginBottom: "8px",
    },
    subtitle: {
      fontSize: "13px",
      color: colors.textSecondary,
      margin: 0,
    },
    card: {
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "24px",
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: 600,
      color: colors.textPrimary,
      margin: "0 0 20px 0",
      paddingBottom: "12px",
      borderBottom: `1px solid ${colors.divider}`,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
      gap: "20px",
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    fullWidth: {
      gridColumn: "1 / -1",
    },
    label: {
      fontSize: "12px",
      fontWeight: 600,
      color: colors.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      padding: "10px 14px",
      background: colors.inputBg,
      border: `1px solid ${colors.inputBorder}`,
      borderRadius: "8px",
      fontSize: "13px",
      color: colors.textPrimary,
      outline: "none",
      transition: "all 0.2s",
    },
    textarea: {
      padding: "10px 14px",
      background: colors.inputBg,
      border: `1px solid ${colors.inputBorder}`,
      borderRadius: "8px",
      fontSize: "13px",
      color: colors.textPrimary,
      outline: "none",
      resize: "vertical",
      minHeight: "80px",
      fontFamily: "inherit",
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
    },
    saveButton: {
      padding: "10px 24px",
      background: colors.buttonPrimaryBg,
      color: colors.buttonPrimaryColor,
      border: "none",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    resetButton: {
      padding: "10px 24px",
      background: colors.buttonSecondaryBg,
      color: colors.buttonSecondaryColor,
      border: `1px solid ${colors.border}`,
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    infoText: {
      fontSize: "11px",
      color: colors.textMuted,
      marginTop: "4px",
    },
    toast: {
      position: "fixed",
      bottom: "80px", // Position above the fixed footer
      right: "24px",
      zIndex: 1100,
      padding: "12px 20px",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 500,
      animation: "slideUp 0.25s ease",
    },
    loadingContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.card}>
            <div style={{ textAlign: "center", padding: "60px" }}>
              <div>Loading settings...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* Only hide scrollbars on the main container, allow scroll on content */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: ${isDark ? "#1e2740" : "#e5e7eb"};
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: ${isDark ? "#4a6cf7" : "#3b82f6"};
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? "#6b8cff" : "#2563eb"};
        }
        
        /* Firefox scrollbar styling */
        * {
          scrollbar-width: thin;
          scrollbar-color: ${isDark ? "#4a6cf7 #1e2740" : "#3b82f6 #e5e7eb"};
        }
        
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        input:focus, textarea:focus {
          border-color: ${colors.inputFocus};
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
          outline: none;
        }
        
        button {
          transition: all 0.2s ease;
        }
        
        button:hover:not(:disabled) {
          transform: translateY(-1px);
          opacity: 0.9;
        }
        
        button:active:not(:disabled) {
          transform: translateY(0);
        }
        
        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      {/* Scrollable Content Area */}
      <div style={styles.scrollableContent}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>General Settings</h1>
          <p style={styles.subtitle}>Manage your company information and social media links</p>
        </div>

        {/* Basic Information Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <span>🏢</span> Basic Information
          </h2>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Name</label>
              <input
                type="text"
                style={styles.input}
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
                placeholder="Enter company name"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Header Text</label>
              <input
                type="text"
                style={styles.input}
                value={formData.headerText}
                onChange={(e) => handleInputChange("headerText", e.target.value)}
                placeholder="Enter header text"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Contact Number</label>
              <input
                type="text"
                style={styles.input}
                value={formData.contact}
                onChange={(e) => handleInputChange("contact", e.target.value)}
                placeholder="Enter contact number"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Call Number</label>
              <input
                type="text"
                style={styles.input}
                value={formData.callNumber}
                onChange={(e) => handleInputChange("callNumber", e.target.value)}
                placeholder="Enter call number"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                style={styles.input}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter email address"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Sales Email</label>
              <input
                type="email"
                style={styles.input}
                value={formData.salesEmail}
                onChange={(e) => handleInputChange("salesEmail", e.target.value)}
                placeholder="Enter sales email"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Domain</label>
              <input
                type="text"
                style={styles.input}
                value={formData.domain}
                onChange={(e) => handleInputChange("domain", e.target.value)}
                placeholder="Enter website domain"
              />
            </div>

            <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
              <label style={styles.label}>Address</label>
              <textarea
                style={styles.textarea}
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Enter company address"
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Social Media Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <span>🌐</span> Social Media Links
          </h2>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Facebook</label>
              <input
                type="url"
                style={styles.input}
                value={formData.facebook}
                onChange={(e) => handleInputChange("facebook", e.target.value)}
                placeholder="https://facebook.com/yourpage"
              />
              <div style={styles.infoText}>Your Facebook page URL</div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Twitter / X</label>
              <input
                type="url"
                style={styles.input}
                value={formData.twitter}
                onChange={(e) => handleInputChange("twitter", e.target.value)}
                placeholder="https://twitter.com/yourhandle"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Instagram</label>
              <input
                type="url"
                style={styles.input}
                value={formData.instagram}
                onChange={(e) => handleInputChange("instagram", e.target.value)}
                placeholder="https://instagram.com/yourhandle"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>LinkedIn</label>
              <input
                type="url"
                style={styles.input}
                value={formData.linkedin}
                onChange={(e) => handleInputChange("linkedin", e.target.value)}
                placeholder="https://linkedin.com/company/yourcompany"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>YouTube</label>
              <input
                type="url"
                style={styles.input}
                value={formData.youtube}
                onChange={(e) => handleInputChange("youtube", e.target.value)}
                placeholder="https://youtube.com/@yourchannel"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>WhatsApp</label>
              <input
                type="url"
                style={styles.input}
                value={formData.whatsapp}
                onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                placeholder="https://wa.me/yournumber"
              />
              <div style={styles.infoText}>WhatsApp chat link</div>
            </div>
          </div>
        </div>

        {/* Footer Settings Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            <span>📝</span> Footer Settings
          </h2>
          <div style={styles.formGrid}>
            <div style={{ ...styles.formGroup, ...styles.fullWidth }}>
              <label style={styles.label}>Footer Text</label>
              <textarea
                style={styles.textarea}
                value={formData.footerText}
                onChange={(e) => handleInputChange("footerText", e.target.value)}
                placeholder="Enter footer text (copyright, disclaimer, etc.)"
                rows="3"
              />
              <div style={styles.infoText}>This text will appear in the website footer</div>
            </div>
          </div>
        </div>
        
        {/* Add a small spacer at the bottom to prevent content from being hidden behind the fixed footer */}
        <div style={{ height: "20px" }} />
      </div>

      {/* Fixed Footer with Action Buttons */}
      <div style={styles.fixedFooter}>
        <div style={styles.buttonGroup}>
          <button
            style={styles.resetButton}
            onClick={resetSettings}
            disabled={!hasChanges() || saving}
          >
            Reset
          </button>
          <button
            style={styles.saveButton}
            onClick={saveSettings}
            disabled={!hasChanges() || saving}
          >
            {saving ? (
              <>
                <span>💾</span> Saving...
              </>
            ) : (
              <>
                <span>💾</span> Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "error" 
              ? (isDark ? "#1a0a0a" : "#fef2f2")
              : toast.type === "info"
              ? (isDark ? "#1a2a3a" : "#e8f4f8")
              : (isDark ? "#0a1a0a" : "#f0fdf4"),
            border: `1px solid ${
              toast.type === "error" 
                ? "rgba(239,68,68,0.4)"
                : toast.type === "info"
                ? "rgba(59,130,246,0.4)"
                : "rgba(34,197,94,0.4)"
            }`,
            color: toast.type === "error"
              ? "#ef4444"
              : toast.type === "info"
              ? "#3b82f6"
              : "#22c55e",
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;